import ReconnectingWebSocket from 'reconnecting-websocket';

import type {
  CoinstoreWsDepthMessage,
  CoinstoreWsKlineMessage,
  CoinstoreWsPong,
  CoinstoreWsSubscribeRequest,
  CoinstoreWsSubscribeResponse,
  CoinstoreWsTickerMessage,
  CoinstoreWsTradeMessage,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

export const COINSTORE_WS_BASE_URL = 'wss://ws.coinstore.com/s/ws';

/**
 * Coinstore WebSocket channel descriptors.
 * @see https://coinstore-openapi.github.io/en/#websocket-ticker-data
 */
export const COINSTORE_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  ticker: {
    channel: 'ticker',
    topic: '<instrumentId>@ticker',
    supportsBatching: true,
    docUrl: 'https://coinstore-openapi.github.io/en/#ticker-information-by-symbol',
  },
  depth: {
    channel: 'depth',
    topic: '<instrumentId>@depth@<levels>',
    supportsBatching: true,
    docUrl: 'https://coinstore-openapi.github.io/en/#depth-information',
  },
  klines: {
    channel: 'kline',
    topic: '<instrumentId>@kline@<interval>',
    supportsBatching: true,
    docUrl: 'https://coinstore-openapi.github.io/en/#k-line-streams',
  },
  trades: {
    channel: 'trade',
    topic: '<instrumentId>@trade',
    supportsBatching: true,
    docUrl: 'https://coinstore-openapi.github.io/en/#trade-by-trade',
  },
};

/**
 * Coinstore WebSocket topology information.
 * Messages are plain JSON (no compression).
 * Server sends ping every 3 minutes; client must respond with pong within 10 minutes.
 * @see https://coinstore-openapi.github.io/en/#access-url
 */
export const COINSTORE_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: COINSTORE_WS_BASE_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 180000, // 3 minutes - server sends ping
  note: 'Plain JSON messages (no compression). Orderbook delivers full snapshots (not incremental). Uses instrumentId (numeric) in channel names. K-line intervals: min_1, min_5, min_15, min_30, hour_1, hour_4, hour_12, day_1, week_1. NOTE: Month (mon_1) NOT supported.',
};

// ========================================
// Helper Types and Constants
// ========================================

/**
 * Coinstore supported kline intervals for WebSocket.
 * NOTE: Month timeframe (mon_1) is NOT supported by Coinstore.
 * @see https://coinstore-openapi.github.io/en/#k-line-streams
 */
export type CoinstoreWsKlineInterval =
  | 'min_1'
  | 'min_5'
  | 'min_15'
  | 'min_30'
  | 'hour_1'
  | 'hour_4'
  | 'hour_12'
  | 'day_1'
  | 'week_1';

/**
 * Coinstore depth levels for depth channel.
 * @see https://coinstore-openapi.github.io/en/#depth-information
 */
export type CoinstoreDepthLevel = 5 | 10 | 20 | 50 | 100;

export type CoinstoreWsStatus =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'error';

// ========================================
// WebSocket Message Handling
// ========================================

/**
 * Type guard for Coinstore WebSocket subscription response.
 */
export function isCoinstoreWsSubscribeResponse(
  value: unknown,
): value is CoinstoreWsSubscribeResponse {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<CoinstoreWsSubscribeResponse>;
  return (
    typeof candidate.S === 'number' &&
    candidate.T === 'resp' &&
    typeof candidate.C === 'number' &&
    typeof candidate.M === 'string'
  );
}

/**
 * Create pong message for Coinstore WebSocket.
 * Must be sent in response to server ping.
 * @see https://coinstore-openapi.github.io/en/#pong
 */
export function createCoinstorePong(): CoinstoreWsPong {
  return {
    op: 'pong',
    epochMillis: Date.now(),
  };
}

/**
 * Create subscribe request for Coinstore WebSocket.
 * @param channels - Array of channel strings (e.g., ["4@ticker", "4@depth@20"])
 * @param params - Optional params (e.g., { size: 2 } for trades)
 */
export function createCoinstoreSubscribeRequest(
  channels: string[],
  params?: Record<string, unknown>,
): CoinstoreWsSubscribeRequest {
  const request: CoinstoreWsSubscribeRequest = {
    op: 'SUB',
    channel: channels,
    id: Date.now(),
  };
  if (params) {
    request.param = params;
  }
  return request;
}

/**
 * Create unsubscribe request for Coinstore WebSocket.
 */
export function createCoinstoreUnsubscribeRequest(channels: string[]): CoinstoreWsSubscribeRequest {
  return {
    op: 'UNSUB',
    channel: channels,
    id: Date.now(),
  };
}

/**
 * Build ticker channel string.
 * @example buildCoinstoreTickerChannel(4) // "4@ticker"
 * @example buildCoinstoreTickerChannel("4") // "4@ticker"
 */
export function buildCoinstoreTickerChannel(instrumentId: number | string): string {
  return `${instrumentId}@ticker`;
}

/**
 * Build kline channel string.
 * @example buildCoinstoreKlineChannel(4, "min_1") // "4@kline@min_1"
 */
export function buildCoinstoreKlineChannel(
  instrumentId: number | string,
  interval: CoinstoreWsKlineInterval,
): string {
  return `${instrumentId}@kline@${interval}`;
}

/**
 * Build depth channel string.
 * @example buildCoinstoreDepthChannel(4, 20) // "4@depth@20"
 */
export function buildCoinstoreDepthChannel(
  instrumentId: number | string,
  levels: CoinstoreDepthLevel = 20,
): string {
  return `${instrumentId}@depth@${levels}`;
}

/**
 * Build trade channel string.
 * @example buildCoinstoreTradeChannel(4) // "4@trade"
 */
export function buildCoinstoreTradeChannel(instrumentId: number | string): string {
  return `${instrumentId}@trade`;
}

// ========================================
// WebSocket Subscription Options
// ========================================

export interface CoinstoreWsSubscriptionOptions {
  /** Channels to subscribe (e.g., ["4@ticker", "4@depth@20"]) */
  channels: string[];
  /** Callback for all messages (caller must handle type routing) */
  onMessage: (data: unknown) => void;
  /** Callback for errors (connection, decode, subscription failures) */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: CoinstoreWsStatus) => void;
  /** Base WebSocket URL (defaults to COINSTORE_WS_BASE_URL) */
  baseUrl?: string;
  /** Optional subscription params */
  params?: Record<string, unknown>;
  /** Custom WebSocket configuration */
  wsConfig?: {
    maxRetries?: number;
    minReconnectionDelay?: number;
  };
}

// ========================================
// Generic WebSocket Subscription
// ========================================

/**
 * Create a generic Coinstore WebSocket subscription.
 * Handles ping/pong heartbeat and automatic reconnection.
 *
 * @returns ReconnectingWebSocket instance (call .close() to unsubscribe)
 */
export function createCoinstoreWsSubscription(
  options: CoinstoreWsSubscriptionOptions,
): ReconnectingWebSocket {
  const {
    channels,
    onMessage,
    onError,
    onStatusChange,
    baseUrl = COINSTORE_WS_BASE_URL,
    params,
    wsConfig = {},
  } = options;

  const config = {
    maxRetries: wsConfig.maxRetries ?? 10,
    minReconnectionDelay: wsConfig.minReconnectionDelay ?? 1000,
  };

  let hasConnectedOnce = false;
  let pongInterval: ReturnType<typeof setInterval> | null = null;

  onStatusChange?.('connecting');

  const ws = new ReconnectingWebSocket(baseUrl, [], config);

  ws.addEventListener('open', () => {
    hasConnectedOnce = true;
    onStatusChange?.('connected');

    // Send subscription request
    const subscribeMsg = createCoinstoreSubscribeRequest(channels, params);
    ws.send(JSON.stringify(subscribeMsg));

    // Start sending pong periodically (every 2 minutes, server timeout is 10 minutes)
    // This keeps the connection alive
    if (pongInterval) {
      clearInterval(pongInterval);
    }
    pongInterval = setInterval(
      () => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(createCoinstorePong()));
        }
      },
      2 * 60 * 1000,
    ); // 2 minutes
  });

  ws.addEventListener('message', (event: MessageEvent) => {
    try {
      const data = event.data as string;

      // Parse JSON message
      const payload = JSON.parse(data) as Record<string, unknown>;

      // Handle ping from server - respond with pong
      if (payload.ping !== undefined) {
        ws.send(JSON.stringify(createCoinstorePong()));
        return;
      }

      // Handle subscription response (T = "resp" or T = "echo")
      if (payload.T === 'resp' || payload.T === 'echo') {
        const response = payload as unknown as CoinstoreWsSubscribeResponse;
        if (response.C !== 200) {
          onError?.(
            new Error(`Coinstore subscription error: code=${response.C}, msg=${response.M}`),
          );
        }
        return;
      }

      // Handle push messages (T = "ticker", "depth", "kline", "trade")
      if (payload.T && payload.S !== undefined) {
        onMessage(payload);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
      onStatusChange?.('error');
    }
  });

  ws.addEventListener('close', () => {
    if (pongInterval) {
      clearInterval(pongInterval);
      pongInterval = null;
    }
    onStatusChange?.(hasConnectedOnce ? 'reconnecting' : 'disconnected');
  });

  ws.addEventListener('error', event => {
    const error = new Error(`Coinstore WebSocket error: ${event.type}`);
    onError?.(error);
    onStatusChange?.('error');
  });

  // Cleanup on close
  const originalClose = ws.close.bind(ws);
  ws.close = (code?: number, reason?: string) => {
    if (pongInterval) {
      clearInterval(pongInterval);
      pongInterval = null;
    }
    originalClose(code, reason);
  };

  return ws;
}

// ========================================
// Ticker Subscription
// ========================================

export interface CoinstoreTickerSubscriptionOptions {
  /** Instrument ID (numeric) */
  instrumentId: number | string;
  /** Callback for ticker updates */
  onMessage: (ticker: CoinstoreWsTickerMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: CoinstoreWsStatus) => void;
}

/**
 * Subscribe to Coinstore ticker updates via WebSocket.
 *
 * @see https://coinstore-openapi.github.io/en/#ticker-information-by-symbol
 */
export function subscribeCoinstoreTicker(
  options: CoinstoreTickerSubscriptionOptions,
): ReconnectingWebSocket {
  const { instrumentId, onMessage, onError, onStatusChange } = options;

  return createCoinstoreWsSubscription({
    channels: [buildCoinstoreTickerChannel(instrumentId)],
    onMessage: data => onMessage(data as CoinstoreWsTickerMessage),
    onError,
    onStatusChange,
  });
}

// ========================================
// Kline (Candlestick) Subscription
// ========================================

export interface CoinstoreKlineSubscriptionOptions {
  /** Instrument ID (numeric) */
  instrumentId: number | string;
  /** Kline interval */
  interval: CoinstoreWsKlineInterval;
  /** Callback for kline updates */
  onMessage: (kline: CoinstoreWsKlineMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: CoinstoreWsStatus) => void;
}

/**
 * Subscribe to Coinstore kline (candlestick) updates via WebSocket.
 *
 * @see https://coinstore-openapi.github.io/en/#k-line-streams
 */
export function subscribeCoinstoreKlines(
  options: CoinstoreKlineSubscriptionOptions,
): ReconnectingWebSocket {
  const { instrumentId, interval, onMessage, onError, onStatusChange } = options;

  return createCoinstoreWsSubscription({
    channels: [buildCoinstoreKlineChannel(instrumentId, interval)],
    onMessage: data => onMessage(data as CoinstoreWsKlineMessage),
    onError,
    onStatusChange,
  });
}

// ========================================
// Depth (Orderbook) Subscription
// ========================================

export interface CoinstoreDepthSubscriptionOptions {
  /** Instrument ID (numeric) */
  instrumentId: number | string;
  /** Depth levels: 5, 10, 20, 50, or 100 (default: 20) */
  levels?: CoinstoreDepthLevel;
  /** Callback for depth updates (full snapshot) */
  onMessage: (depth: CoinstoreWsDepthMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: CoinstoreWsStatus) => void;
}

/**
 * Subscribe to Coinstore depth (orderbook) updates via WebSocket.
 * Note: Coinstore sends full snapshots, not incremental updates.
 *
 * @see https://coinstore-openapi.github.io/en/#depth-information
 */
export function subscribeCoinstoreDepth(
  options: CoinstoreDepthSubscriptionOptions,
): ReconnectingWebSocket {
  const { instrumentId, levels = 20, onMessage, onError, onStatusChange } = options;

  return createCoinstoreWsSubscription({
    channels: [buildCoinstoreDepthChannel(instrumentId, levels)],
    onMessage: data => onMessage(data as CoinstoreWsDepthMessage),
    onError,
    onStatusChange,
  });
}

// ========================================
// Trade Subscription
// ========================================

export interface CoinstoreTradeSubscriptionOptions {
  /** Instrument ID (numeric) */
  instrumentId: number | string;
  /** Callback for trade updates */
  onMessage: (trade: CoinstoreWsTradeMessage) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: CoinstoreWsStatus) => void;
  /** Number of initial trades to receive (default: 50) */
  size?: number;
}

/**
 * Subscribe to Coinstore public trade updates via WebSocket.
 *
 * @see https://coinstore-openapi.github.io/en/#trade-by-trade
 */
export function subscribeCoinstoreTrades(
  options: CoinstoreTradeSubscriptionOptions,
): ReconnectingWebSocket {
  const { instrumentId, onMessage, onError, onStatusChange, size } = options;

  return createCoinstoreWsSubscription({
    channels: [buildCoinstoreTradeChannel(instrumentId)],
    onMessage: data => onMessage(data as CoinstoreWsTradeMessage),
    onError,
    onStatusChange,
    params: size ? { size } : undefined,
  });
}

// ========================================
// Multi-Channel Subscription
// ========================================

export interface CoinstoreMultiChannelSubscriptionOptions {
  /** Array of channel strings (e.g., ["4@ticker", "4@depth@20"]) */
  channels: string[];
  /** Callback for all messages (caller must handle type routing) */
  onMessage: (message: unknown) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Callback for connection status changes */
  onStatusChange?: (status: CoinstoreWsStatus) => void;
}

/**
 * Subscribe to multiple Coinstore channels via a single WebSocket connection.
 * Useful for subscribing to ticker, klines, depth, and trades together.
 *
 * @example
 * const ws = subscribeCoinstoreMultiChannel({
 *   channels: [
 *     buildCoinstoreTickerChannel(4),
 *     buildCoinstoreKlineChannel(4, "min_1"),
 *     buildCoinstoreDepthChannel(4, 20),
 *     buildCoinstoreTradeChannel(4),
 *   ],
 *   onMessage: (msg) => {
 *     const payload = msg as Record<string, unknown>;
 *     switch (payload.T) {
 *       case "ticker": // handle ticker
 *       case "depth": // handle depth
 *       case "kline": // handle kline
 *       case "trade": // handle trade
 *     }
 *   }
 * });
 */
export function subscribeCoinstoreMultiChannel(
  options: CoinstoreMultiChannelSubscriptionOptions,
): ReconnectingWebSocket {
  const { channels, onMessage, onError, onStatusChange } = options;

  return createCoinstoreWsSubscription({
    channels,
    onMessage,
    onError,
    onStatusChange,
  });
}
